import { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, ChevronRight, Loader2, Upload, X } from 'lucide-react';
import { cn } from '@siliconx/shared/ui/utils';
import { Button } from '@siliconx/shared/ui/Button';
import type {
  ErpMappingTemplateRow,
  ErpMatchRunResponse,
  ErpMatchStatus,
  ErpRegisterTargetField,
  ErpSourceSystem,
} from '@siliconx/shared';
import { ERP_REGISTER_TARGET_FIELDS, suggestErpHeaderMapping } from '@siliconx/shared';
import { api, throwIfNotOk } from '../../lib/api';
import { ERP_SOURCE_SYSTEMS_UI, ErpBrandIcon } from './ErpBrandIcon';
import { readRegisterFileHeaders } from './erpHeaders';

/**
 * ERP register import wizard (SIL-270, W4-T10 of
 * resources/plans/erp-register-import-spec.md decision 6/7):
 *
 *   select (preset + file, or a saved template) -> map (deterministic
 *   auto-suggest + GAAP-book column picks, optional save-as-template) ->
 *   processing (presign -> S3 PUT -> parse -> match-run) -> summary (counts
 *   + optional bulk promote of clean matches).
 *
 * Scope note (collision awareness, SIL-271 runs in parallel): this wizard
 * ends at the match summary. It does NOT render the interactive triage
 * queue (per-row conflict/lump resolution UI) — that surface, plus the
 * Financials empty-state deep links and coverage stat, belongs to SIL-271.
 * The "Promote matched rows" action here only ever touches rows already at
 * match_status='matched'/'conflict' via the existing bulk promote endpoint;
 * everything needing a human triage decision is left for that other screen.
 */

type WizardStep = 'select' | 'map' | 'processing' | 'summary';

const REGISTER_EXTS = ['csv', 'xlsx'] as const;
type RegisterExt = (typeof REGISTER_EXTS)[number];

const TARGET_FIELD_LABELS: Record<ErpRegisterTargetField, string> = {
  serial_number: 'Serial number',
  asset_tag: 'Asset tag',
  po_ref: 'PO reference',
  invoice_ref: 'Invoice reference',
  quantity: 'Quantity (lump lines)',
  description: 'Description',
  purchase_price_usd: 'Purchase price (USD)',
  purchased_at: 'Purchase date',
  in_service_at: 'In-service date',
  erp_reported_nbv: 'Net book value (GAAP book)',
  erp_nbv_as_of: 'NBV as-of date',
  depreciation_method: 'Depreciation method',
  useful_life_years: 'Useful life (years)',
};

// Fields where the source register commonly carries more than one book
// (GAAP vs tax vs local statutory) — decision 5: "GAAP book only — mapping
// picks one book if the export has several." Flagged in the mapper so an
// ambiguous auto-suggest reads as a deliberate book choice, not a bug.
const BOOK_SENSITIVE_FIELDS = new Set<ErpRegisterTargetField>([
  'erp_reported_nbv',
  'erp_nbv_as_of',
  'depreciation_method',
  'useful_life_years',
]);

const STATUS_LABELS: Record<ErpMatchStatus, string> = {
  matched: 'Matched (serial → asset tag → PO)',
  lump_partial: 'Lump lines needing allocation',
  ambiguous: 'Ambiguous / needs triage',
  conflict: 'Conflicts with manual data',
  unmatched: 'Unmatched',
  pending: 'Heterogeneous lumps (pending)',
  dismissed: 'Dismissed',
};

const STATUS_ORDER: ErpMatchStatus[] = [
  'matched',
  'lump_partial',
  'ambiguous',
  'conflict',
  'unmatched',
  'pending',
  'dismissed',
];

function extFromFilename(name: string): RegisterExt | null {
  const lower = name.toLowerCase();
  if (lower.endsWith('.csv')) return 'csv';
  if (lower.endsWith('.xlsx')) return 'xlsx';
  return null;
}

function contentTypeFor(ext: RegisterExt): string {
  return ext === 'csv' ? 'text/csv' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
}

type ColumnMapping = Partial<Record<ErpRegisterTargetField, string>>;

export function ErpImportWizard({ onClose }: { onClose: () => void }) {
  const [step, setStep] = useState<WizardStep>('select');
  const [err, setErr] = useState<string | null>(null);

  // --- select step ---
  const [sourceSystem, setSourceSystem] = useState<ErpSourceSystem | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [ext, setExt] = useState<RegisterExt | null>(null);
  const [templates, setTemplates] = useState<ErpMappingTemplateRow[] | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');

  // --- map step ---
  const [headers, setHeaders] = useState<string[]>([]);
  const [mapping, setMapping] = useState<ColumnMapping>({});
  const [ambiguousTargets, setAmbiguousTargets] = useState<Set<ErpRegisterTargetField>>(new Set());
  const [readingHeaders, setReadingHeaders] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templateSaved, setTemplateSaved] = useState(false);

  // --- processing/summary ---
  const [processingLabel, setProcessingLabel] = useState('');
  const [batchId, setBatchId] = useState<string | null>(null);
  const [rowCountTotal, setRowCountTotal] = useState(0);
  const [matchSummary, setMatchSummary] = useState<ErpMatchRunResponse | null>(null);
  const [promoting, setPromoting] = useState(false);
  const [promoteResult, setPromoteResult] = useState<{ promoted: number; conflicts: number; errors: number } | null>(
    null,
  );

  useEffect(() => {
    api.api.client.erp.templates
      .$get()
      .then(async (res) => {
        await throwIfNotOk(res);
        const { data } = await res.json();
        setTemplates(data);
      })
      .catch(() => setTemplates([]));
  }, []);

  function handleFileChosen(f: File) {
    const detectedExt = extFromFilename(f.name);
    if (!detectedExt) {
      setErr('Only .csv or .xlsx files are supported.');
      return;
    }
    setErr(null);
    setFile(f);
    setExt(detectedExt);
  }

  async function goToMapping() {
    if (!file || !ext) {
      setErr('Choose a file first.');
      return;
    }
    setErr(null);
    setReadingHeaders(true);
    try {
      const hdrs = await readRegisterFileHeaders(file, ext);
      if (hdrs.length === 0) {
        setErr('Could not find a header row in this file.');
        return;
      }
      setHeaders(hdrs);

      const template = templates?.find((t) => t.id === selectedTemplateId) ?? null;
      if (template) {
        const applied: ColumnMapping = {};
        for (const field of ERP_REGISTER_TARGET_FIELDS) {
          const h = template.columnMapping[field];
          if (h) applied[field] = h;
        }
        setMapping(applied);
        setAmbiguousTargets(new Set());
      } else {
        const suggestions = suggestErpHeaderMapping(hdrs, sourceSystem ?? undefined);
        const byTarget = new Map<ErpRegisterTargetField, string[]>();
        for (const [header, target] of Object.entries(suggestions)) {
          if (!target) continue;
          const list = byTarget.get(target) ?? [];
          list.push(header);
          byTarget.set(target, list);
        }
        const initial: ColumnMapping = {};
        const ambiguous = new Set<ErpRegisterTargetField>();
        for (const [target, hdrsForTarget] of byTarget) {
          if (hdrsForTarget.length === 1) initial[target] = hdrsForTarget[0];
          else ambiguous.add(target);
        }
        setMapping(initial);
        setAmbiguousTargets(ambiguous);
      }
      setStep('map');
    } catch (ex: unknown) {
      setErr(`Could not read this file's columns: ${(ex as Error).message}`);
    } finally {
      setReadingHeaders(false);
    }
  }

  function setFieldMapping(target: ErpRegisterTargetField, header: string) {
    setMapping((prev) => {
      const next = { ...prev };
      if (header) next[target] = header;
      else delete next[target];
      return next;
    });
    setAmbiguousTargets((prev) => {
      if (!prev.has(target)) return prev;
      const next = new Set(prev);
      next.delete(target);
      return next;
    });
  }

  async function saveAsTemplate() {
    if (!templateName.trim()) {
      setErr('Give the template a name first.');
      return;
    }
    setSavingTemplate(true);
    setErr(null);
    try {
      const res = await api.api.client.erp.templates.$post({
        json: {
          name: templateName.trim(),
          sourceSystem: sourceSystem ?? null,
          columnMapping: mapping,
        },
      });
      await throwIfNotOk(res);
      setTemplateSaved(true);
    } catch (ex: unknown) {
      setErr((ex as Error).message);
    } finally {
      setSavingTemplate(false);
    }
  }

  async function runImport() {
    if (!file || !ext) return;
    setErr(null);
    setStep('processing');
    try {
      setProcessingLabel('Requesting upload URL…');
      const presignRes = await api.api.client['erp-register'].batches.presign.$post({
        json: { filename: file.name, ext },
      });
      if (!presignRes.ok) {
        await throwIfNotOk(presignRes);
        throw new Error(`HTTP ${presignRes.status}`);
      }
      const presign = await presignRes.json();
      setBatchId(presign.batchId);

      setProcessingLabel('Uploading file…');
      const put = await fetch(presign.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': contentTypeFor(ext) },
        body: file,
      });
      if (!put.ok) throw new Error(`Upload failed (HTTP ${put.status})`);

      setProcessingLabel('Parsing register…');
      // source header -> target field (inverse of `mapping`) — the shape
      // apps/api/src/routes/client/erp-register.ts's parseBodySchema expects.
      const columnMapping: Record<string, ErpRegisterTargetField> = {};
      for (const [target, header] of Object.entries(mapping) as [ErpRegisterTargetField, string][]) {
        if (header && headers.includes(header)) columnMapping[header] = target;
      }
      const parseRes = await api.api.client['erp-register'].batches[':batchId'].parse.$post({
        param: { batchId: presign.batchId },
        json: { key: presign.key, ext, columnMapping },
      });
      if (!parseRes.ok) {
        await throwIfNotOk(parseRes);
        throw new Error(`HTTP ${parseRes.status}`);
      }
      const parseResult = await parseRes.json();
      setRowCountTotal(parseResult.rowCountTotal);

      setProcessingLabel('Matching against your fleet…');
      const matchRes = await api.api.client.erp.batches[':batchId']['match-run'].$post({
        param: { batchId: presign.batchId },
      });
      if (!matchRes.ok) {
        await throwIfNotOk(matchRes);
        throw new Error(`HTTP ${matchRes.status}`);
      }
      const matchResult: ErpMatchRunResponse = await matchRes.json();
      setMatchSummary(matchResult);
      setStep('summary');
    } catch (ex: unknown) {
      setErr((ex as Error).message);
      setStep('map');
    }
  }

  async function promoteMatched() {
    if (!batchId) return;
    setPromoting(true);
    setErr(null);
    try {
      const res = await api.api.client['erp-register'].batches[':batchId'].promote.$post({
        param: { batchId },
      });
      if (!res.ok) {
        await throwIfNotOk(res);
        throw new Error(`HTTP ${res.status}`);
      }
      const body = await res.json();
      setPromoteResult({ promoted: body.promoted, conflicts: body.conflicts.length, errors: body.errors.length });
    } catch (ex: unknown) {
      setErr((ex as Error).message);
    } finally {
      setPromoting(false);
    }
  }

  const mappedCount = Object.keys(mapping).length;
  const needsTriage =
    matchSummary != null
      ? (matchSummary.statusCounts.lump_partial ?? 0) +
        (matchSummary.statusCounts.ambiguous ?? 0) +
        (matchSummary.statusCounts.conflict ?? 0) +
        (matchSummary.statusCounts.pending ?? 0)
      : 0;

  return (
    <div className="space-y-4 rounded-md border border-zinc-200 bg-zinc-50 p-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-zinc-900">Import ERP fixed-asset register</h3>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close import wizard"
          className="rounded-md p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600"
        >
          <X className="size-4" />
        </button>
      </div>

      {err && (
        <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
          <span>{err}</span>
        </div>
      )}

      {step === 'select' && (
        <div className="space-y-4">
          <div>
            <div className="mb-1.5 text-xs font-medium text-zinc-600">Which ERP is this register from?</div>
            <div className="flex flex-wrap gap-2">
              {ERP_SOURCE_SYSTEMS_UI.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setSourceSystem(s.id)}
                  className={cn(
                    'flex items-center gap-2 rounded-md border px-3 py-2 text-xs font-medium',
                    sourceSystem === s.id
                      ? 'border-brand-500 bg-brand-50 text-brand-700 ring-1 ring-brand-500'
                      : 'border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50',
                  )}
                >
                  <ErpBrandIcon system={s.id} size={18} />
                  {s.label}
                </button>
              ))}
            </div>
            <p className="mt-1 text-xs text-zinc-400">
              Optional — used to seed the column-mapper's auto-suggest. You can still map any column by hand.
            </p>
          </div>

          {templates && templates.length > 0 && (
            <div>
              <label className="mb-1 block text-xs font-medium text-zinc-600">
                Use a saved mapping template (optional)
              </label>
              <select
                value={selectedTemplateId}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
                className="w-full rounded-md border border-zinc-300 px-2.5 py-1.5 text-xs"
              >
                <option value="">None — auto-suggest from headers</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="mb-1 block text-xs font-medium text-zinc-600">Register export file</label>
            <label
              className={cn(
                'flex cursor-pointer items-center justify-center gap-2 rounded-md border-2 border-dashed border-zinc-300 bg-white px-4 py-6 text-xs text-zinc-500 hover:border-brand-400 hover:bg-brand-50/40',
              )}
            >
              <Upload className="size-4" />
              {file ? file.name : 'Choose a .csv or .xlsx file'}
              <input
                type="file"
                accept=".csv,.xlsx"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleFileChosen(f);
                }}
              />
            </label>
          </div>

          <Button size="sm" className="gap-1 inline-flex items-center"
            onClick={goToMapping}
            disabled={!file || readingHeaders}
          >
            {readingHeaders ? <Loader2 className="size-3.5 animate-spin" /> : <ChevronRight className="size-3.5" />}
            {readingHeaders ? 'Reading columns…' : 'Continue to column mapping'}
          </Button>
        </div>
      )}

      {step === 'map' && (
        <div className="space-y-3">
          <p className="text-xs text-zinc-500">
            Map each field to a column in your file. Fields left unmapped are skipped. Where a field shows more than
            one candidate column (e.g. multiple book values), pick the <strong>GAAP book</strong> column explicitly.
          </p>

          <div className="max-h-96 space-y-1.5 overflow-y-auto rounded-md border border-zinc-200 bg-white p-2">
            <div className="flex items-center gap-2 border-b border-zinc-100 px-1 pb-1.5 text-xs font-semibold uppercase tracking-wide text-zinc-400">
              <div className="w-44 shrink-0">SiliconX field</div>
              <div className="flex-1">Column from your ERP file</div>
            </div>
            {ERP_REGISTER_TARGET_FIELDS.map((field) => (
              <div key={field} className="flex items-center gap-2 text-xs">
                <div className="w-44 shrink-0 text-zinc-600">
                  {TARGET_FIELD_LABELS[field]}
                  {BOOK_SENSITIVE_FIELDS.has(field) && ambiguousTargets.has(field) && (
                    <span className="ml-1 inline-flex items-center gap-0.5 text-amber-600" title="Multiple candidate columns found — choose the GAAP book column">
                      <AlertTriangle className="size-3" />
                    </span>
                  )}
                </div>
                <select
                  value={mapping[field] ?? ''}
                  onChange={(e) => setFieldMapping(field, e.target.value)}
                  className={cn(
                    'flex-1 rounded border px-2 py-1 text-xs',
                    ambiguousTargets.has(field) ? 'border-amber-400' : 'border-zinc-200',
                  )}
                >
                  <option value="">Not mapped</option>
                  {headers.map((h) => (
                    <option key={h} value={h}>
                      {h}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-2 rounded-md border border-zinc-200 bg-white p-2">
            <input
              type="text"
              value={templateName}
              onChange={(e) => {
                setTemplateName(e.target.value);
                setTemplateSaved(false);
              }}
              placeholder="Template name (e.g. NetSuite quarterly export)"
              className="flex-1 rounded border border-zinc-200 px-2 py-1 text-xs"
            />
            <button
              type="button"
              onClick={saveAsTemplate}
              disabled={savingTemplate || mappedCount === 0}
              className="whitespace-nowrap rounded-md border border-zinc-300 px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
            >
              {savingTemplate ? 'Saving…' : templateSaved ? 'Saved ✓' : 'Save as template'}
            </button>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setStep('select')}
              className="rounded-md px-3 py-1.5 text-xs font-medium text-zinc-500 hover:bg-zinc-100"
            >
              Back
            </button>
            <Button size="sm" className="gap-1 inline-flex items-center"
              onClick={runImport}
              disabled={mappedCount === 0}
            >
              <ChevronRight className="size-3.5" />
              Upload &amp; match
            </Button>
          </div>
        </div>
      )}

      {step === 'processing' && (
        <div className="flex items-center gap-2 rounded-md border border-zinc-200 bg-white p-4 text-xs text-zinc-500">
          <Loader2 className="size-4 animate-spin text-brand-600" />
          {processingLabel}
        </div>
      )}

      {step === 'summary' && matchSummary && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-800">
            <CheckCircle2 className="size-3.5" />
            Parsed {rowCountTotal} row{rowCountTotal !== 1 ? 's' : ''} and ran the match pass.
          </div>

          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {STATUS_ORDER.filter((s) => (matchSummary.statusCounts[s] ?? 0) > 0).map((s) => (
              <div key={s} className="rounded-md border border-zinc-200 bg-white p-2.5">
                <div className="text-lg font-semibold text-zinc-900">{matchSummary.statusCounts[s] ?? 0}</div>
                <div className="text-xs text-zinc-500">{STATUS_LABELS[s]}</div>
              </div>
            ))}
          </div>

          {(matchSummary.statusCounts.matched ?? 0) > 0 && (
            <div className="rounded-md border border-zinc-200 bg-white p-3">
              {promoteResult ? (
                <p className="text-xs text-zinc-600">
                  Promoted {promoteResult.promoted} asset{promoteResult.promoted !== 1 ? 's' : ''}.
                  {promoteResult.conflicts > 0 && ` ${promoteResult.conflicts} row(s) need conflict triage.`}
                  {promoteResult.errors > 0 && ` ${promoteResult.errors} row(s) failed to promote.`}
                </p>
              ) : (
                <>
                  <p className="mb-2 text-xs text-zinc-500">
                    {matchSummary.statusCounts.matched} row{matchSummary.statusCounts.matched !== 1 ? 's' : ''} matched
                    cleanly and can be promoted into your assets' financial data now.
                  </p>
                  <Button size="sm" className="gap-1 inline-flex items-center"
                    onClick={promoteMatched}
                    disabled={promoting}
                  >
                    {promoting && <Loader2 className="size-3.5 animate-spin" />}
                    {promoting ? 'Promoting…' : `Promote ${matchSummary.statusCounts.matched} matched row(s)`}
                  </Button>
                </>
              )}
            </div>
          )}

          {needsTriage > 0 && (
            <p className="text-xs text-zinc-400">
              {needsTriage} row{needsTriage !== 1 ? 's' : ''} need a triage decision (lumps, conflicts, or ambiguous
              matches) — resolve these from the Financials triage queue once it&apos;s live for this batch.
            </p>
          )}

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-zinc-300 px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
